import React from 'react';
import { 
  ArrowRight, Award, BookOpen, Calendar, CheckCircle2, 
  ChevronRight, FileText, ShieldCheck, Sparkles, Star, Users 
} from 'lucide-react';
import { INSTITUTION_INFO } from '../data/portalData';

export const Hero = ({ setActiveView }) => {
  return (
    <section className="relative overflow-hidden aksu-hero-gradient bg-gradient-to-br from-[#030F1C] via-[#06182B] to-[#0B2644] text-white pt-12 pb-20 lg:pt-16 lg:pb-28 border-b-4 border-[#7A470E]">
      {/* Background Decorative Grid and Lighting in Executive Navy & Prestige Gold */}
      <div className="absolute inset-0 subtle-grid-dark opacity-35 pointer-events-none" />
      <div className="absolute -top-40 -left-40 w-96 h-96 bg-[#7A470E]/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 -right-40 w-96 h-96 bg-[#133562]/30 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Top Executive Pill with Business School Colors */}
        <div className="inline-flex flex-wrap items-center gap-1.5 px-3.5 py-1.5 rounded-2xl sm:rounded-full bg-[#06182B]/90 border border-[#7A470E]/60 text-white text-[11px] sm:text-xs font-semibold mb-6 backdrop-blur-md shadow-lg">
          <span className="flex h-2.5 w-2.5 rounded-full bg-[#B87A28] animate-pulse shrink-0" />
          <span className="tracking-wide uppercase font-bold text-[#B87A28] font-sc">An Executive Programme</span>
          <span className="text-[#7A470E] hidden sm:inline">•</span>
          <span className="text-slate-200 font-sc">2025/2026 & 2026/2027 Academic Sessions</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Left Column: Headlines & Executive Value Proposition */}
          <div className="lg:col-span-7 space-y-6">
            
            {/* Prominent Business School Brand Badge */}
            <div className="flex items-center space-x-3 pb-1">
              <img 
                src="/Logo.png" 
                alt="Akwa Ibom State University Official Crest" 
                className="w-16 h-16 sm:w-20 sm:h-20 object-contain logo-glow select-none" 
              />
              <div className="border-l-2 border-[#7A470E] pl-3">
                <span className="text-xs uppercase font-extrabold tracking-wider text-slate-300 font-sc block">
                  Akwa Ibom State University
                </span>
                <span className="text-base sm:text-lg font-black text-[#B87A28] font-sc block">
                  BUSINESS SCHOOL
                </span>
                <span className="text-[11px] text-slate-300 font-bold uppercase tracking-widest font-sc">
                  Knowledge · Enterprise · Leadership
                </span>
              </div>
            </div>

            <h1 className="text-3xl sm:text-5xl xl:text-6xl font-black tracking-tight font-sc leading-tight text-white">
              Shaping <span className="text-[#B87A28] drop-shadow-md">Visionary Leaders</span> for Emerging Global Economies
            </h1>

            <p className="text-slate-200 text-base sm:text-lg leading-relaxed font-normal max-w-2xl">
              The <strong className="text-white font-bold">AKSU Business School</strong> is designed specifically as an 
              executive programme for business executives, captains of industry, heads of government MDAs, political appointees, 
              and senior decision-makers. Delivered in flexible Full-time/Virtual/Hybrid modules for professionals who demand 
              uncompromising academic excellence.
            </p>

            {/* Core Pillars / Badges in Translucent Navy & Gold Card */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 rounded-2xl translucent-card-navy max-w-xl shadow-lg">
              <div className="flex flex-col">
                <span className="text-xs uppercase font-extrabold tracking-wider text-[#B87A28] font-sc">Knowledge</span>
                <span className="text-xs text-slate-300">Rigorous Doctoral & Master's Research</span>
              </div>
              <div className="flex flex-col border-t sm:border-t-0 sm:border-l border-[#7A470E]/30 pt-2 sm:pt-0 sm:pl-3">
                <span className="text-xs uppercase font-extrabold tracking-wider text-white font-sc">Enterprise</span>
                <span className="text-xs text-slate-300">Pragmatic C-Suite Problem Solving</span>
              </div>
              <div className="flex flex-col border-t sm:border-t-0 sm:border-l border-[#7A470E]/30 pt-2 sm:pt-0 sm:pl-3">
                <span className="text-xs uppercase font-extrabold tracking-wider text-[#B87A28] font-sc">Leadership</span>
                <span className="text-xs text-slate-300">Strategic Statecraft & Governance</span>
              </div>
            </div>

            {/* Primary Action Buttons in Business School Palette */}
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <button
                onClick={() => setActiveView('apply')}
                className="px-6 py-3.5 rounded-xl font-black text-sm sm:text-base text-white bg-[#7A470E] hover:bg-[#5C3306] transition-all shadow-lg shadow-black/40 hover:shadow-xl hover:scale-[1.02] flex items-center group cursor-pointer border border-[#B87A28]"
              >
                <span className="font-sc tracking-wide">Apply for Executive Admission</span>
                <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
              </button>

              <button
                onClick={() => setActiveView('admission-advert')}
                className="px-6 py-3.5 rounded-xl font-bold text-sm sm:text-base text-white bg-[#0B2644] hover:bg-[#133562] border border-[#7A470E]/60 transition-all flex items-center space-x-2 shadow-md cursor-pointer hover:scale-[1.02]"
              >
                <FileText className="w-4 h-4 text-[#B87A28]" />
                <span className="font-sc tracking-wide">View Admission Advert</span>
              </button>
            </div>

            {/* Quick Micro-Trust Signals in Translucent Badges */}
            <div className="flex flex-wrap items-center gap-3 pt-4 text-xs text-slate-200">
              <div className="flex items-center space-x-1.5 px-3 py-1.5 rounded-full translucent-card-navy shadow-sm">
                <ShieldCheck className="w-4 h-4 text-[#B87A28]" />
                <span>NUC Accredited Postgrad School</span>
              </div>
              <div className="flex items-center space-x-1.5 px-3 py-1.5 rounded-full translucent-card-navy shadow-sm">
                <CheckCircle2 className="w-4 h-4 text-[#B87A28]" />
                <span>Paystack Instant Verification</span>
              </div>
              <div className="flex items-center space-x-1.5 px-3 py-1.5 rounded-full translucent-card-navy shadow-sm">
                <Calendar className="w-4 h-4 text-[#7A470E]" />
                <span>Full-time / Virtual / Hybrid</span>
              </div>
            </div>

          </div>

          {/* Right Column: Interactive Executive Flyer & Portal Dossier */}
          <div className="lg:col-span-5 relative">
            <div className="relative mx-auto max-w-md lg:max-w-none">
              
              {/* Luxury Frame in Business School Navy and Gold */}
              <div className="rounded-3xl p-1 bg-gradient-to-b from-[#7A470E] via-[#0B2644] to-[#B87A28] shadow-2xl backdrop-blur-xl">
                <div className="bg-[#06182B] rounded-3xl p-5 sm:p-6 space-y-5 border border-[#7A470E]/30">
                  
                  {/* Card Header with School Crest & University Logo */}
                  <div className="flex items-center justify-between pb-4 border-b border-[#7A470E]/30">
                    <div className="flex items-center space-x-3">
                      <img 
                        src="/Logo.png" 
                        alt="AKSU Official Crest" 
                        className="w-12 h-12 object-contain logo-glow"
                      />
                      <div>
                        <div className="text-xs uppercase font-extrabold tracking-wider text-[#B87A28] font-sc">
                          Official Admission Circular
                        </div>
                        <div className="text-sm font-bold text-white font-sc">
                          Executive Academic Session
                        </div>
                      </div>
                    </div>
                    <span className="px-2.5 py-1 text-[11px] font-black rounded-full bg-[#7A470E] text-white border border-[#B87A28] font-sc">
                      Admissions Open
                    </span>
                  </div>

                  {/* Interactive Admission Advert Announcement Box (No raw image, dedicated page CTA) */}
                  <div 
                    onClick={() => setActiveView('admission-advert')}
                    className="relative group overflow-hidden rounded-2xl border-2 border-[#7A470E] bg-gradient-to-br from-[#0B2644] to-[#030F1C] p-5 cursor-pointer shadow-lg hover:border-[#B87A28] transition-all"
                  >
                    <div className="flex items-center justify-between text-xs text-[#B87A28] mb-2 font-sc font-bold">
                      <span className="flex items-center">
                        <FileText className="w-3.5 h-3.5 mr-1 text-[#7A470E]" />
                        Official Admission Advert
                      </span>
                      <span className="bg-[#7A470E] text-white text-[10px] font-black px-2 py-0.5 rounded shadow font-sc">
                        Dedicated Page
                      </span>
                    </div>

                    <h3 className="text-lg font-black text-white font-sc group-hover:text-[#B87A28] transition-colors leading-snug">
                      Postgraduate, Professional & Diploma Programmes
                    </h3>

                    <p className="text-xs text-slate-300 mt-2 leading-relaxed">
                      Structured Full-time/Virtual/Hybrid executive programmes across Business Administration, Public Administration, and Accounting & Finance.
                    </p>

                    <div className="mt-4 pt-3 border-t border-[#7A470E]/30 flex items-center justify-between text-xs text-[#B87A28] font-sc font-bold">
                      <span>Explore Official Admission Advert</span>
                      <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform text-[#7A470E]" />
                    </div>
                  </div>

                  {/* Highlights Summary per Advert */}
                  <div className="space-y-2 text-xs text-slate-300">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between py-1 border-b border-slate-800 gap-0.5 sm:gap-2">
                      <span className="text-slate-400 shrink-0">Target Candidates:</span>
                      <span className="font-semibold text-white sm:text-right">CEOs, MDs, MDA Heads, Directors</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between py-1 border-b border-slate-800 gap-0.5 sm:gap-2">
                      <span className="text-slate-400 shrink-0">Academic Structure:</span>
                      <span className="font-bold text-[#B87A28] sm:text-right">Full-time / Virtual / Hybrid</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between py-1 border-b border-slate-800 gap-0.5 sm:gap-2">
                      <span className="text-slate-400 shrink-0">Lectures Commence:</span>
                      <span className="font-bold text-[#7A470E] sm:text-right">October 2026</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between py-1 gap-0.5 sm:gap-2">
                      <span className="text-slate-400 shrink-0">Application Fee:</span>
                      <span className="font-black text-white sm:text-right">₦25,000 (Paystack Gateway)</span>
                    </div>
                  </div>

                  {/* Fast Action in Card */}
                  <div className="pt-2 grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setActiveView('apply')}
                      className="py-2.5 px-3 bg-[#7A470E] hover:bg-[#5C3306] text-white font-extrabold rounded-xl text-xs transition-colors text-center shadow-md flex items-center justify-center space-x-1 cursor-pointer font-sc"
                    >
                      <span>Start Application</span>
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setActiveView('admission-advert')}
                      className="py-2.5 px-3 bg-[#0B2644] hover:bg-[#133562] text-white font-bold rounded-xl text-xs transition-colors text-center border border-[#7A470E]/40 flex items-center justify-center space-x-1 cursor-pointer font-sc"
                    >
                      <FileText className="w-3.5 h-3.5 mr-1 text-[#B87A28]" />
                      <span>Full Advert Page</span>
                    </button>
                  </div>

                </div>
              </div>

            </div>
          </div>

        </div>

        {/* Executive Impact Statistics Ribbon */}
        <div className="mt-12 sm:mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 sm:gap-6 pt-6 sm:pt-8 border-t border-[#7A470E]/30">
          <div className="translucent-card-navy rounded-2xl p-4 sm:p-5 hover:scale-[1.02] transition-all">
            <div className="text-2xl sm:text-3xl lg:text-4xl font-black text-[#B87A28] font-sc">
              100%
            </div>
            <div className="text-xs sm:text-sm font-bold text-white mt-1 font-sc">
              NUC Accredited
            </div>
            <div className="text-[11px] sm:text-xs text-slate-300 mt-0.5">
              Rigorous national compliance standards
            </div>
          </div>

          <div className="translucent-card-navy rounded-2xl p-4 sm:p-5 hover:scale-[1.02] transition-all">
            <div className="text-2xl sm:text-3xl lg:text-4xl font-black text-[#B87A28] font-sc break-words">
              3 Disciplines
            </div>
            <div className="text-xs sm:text-sm font-bold text-white mt-1 font-sc">
              Specialised Departments
            </div>
            <div className="text-[11px] sm:text-xs text-slate-300 mt-0.5 break-words">
              Business Admin, Public Admin, Accounting & Finance
            </div>
          </div>

          <div className="translucent-card-navy rounded-2xl p-4 sm:p-5 hover:scale-[1.02] transition-all">
            <div className="text-2xl sm:text-3xl lg:text-4xl font-black text-[#B87A28] font-sc break-words">
              4 Distinct Tracks
            </div>
            <div className="text-xs sm:text-sm font-bold text-white mt-1 font-sc">
              PhD · DBA · MBA · PGD
            </div>
            <div className="text-[11px] sm:text-xs text-slate-300 mt-0.5">
              Plus executive diplomas and certifications
            </div>
          </div>

          <div className="translucent-card-navy rounded-2xl p-4 sm:p-5 hover:scale-[1.02] transition-all">
            <div className="text-2xl sm:text-3xl lg:text-4xl font-black text-[#B87A28] font-sc">
              Hybrid
            </div>
            <div className="text-xs sm:text-sm font-bold text-white mt-1 font-sc">
              Weekend Lectures
            </div>
            <div className="text-[11px] sm:text-xs text-slate-300 mt-0.5">
              Obio Akpa, Uyo Centre & Interactive LMS
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};
